import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog as HeadlessDialog } from '@headlessui/react';
import { CommunityScheduledQueue, useScheduledQueueFlow } from '@shared/dream-components';

import type { CommunityScheduledPost } from '~/interfaces/community';

import { ConfirmDialog } from '../ui/ConfirmDialog';

import useDeleteCommunityScheduledPostMutation from '../../_hooks/useDeleteCommunityScheduledPostMutation';
import usePublishCommunityScheduledPostMutation from '../../_hooks/usePublishCommunityScheduledPostMutation';
import useRescheduleCommunityScheduledPostMutation from '../../_hooks/useRescheduleCommunityScheduledPostMutation';
import useTakeOverCommunityScheduledPostMutation from '../../_hooks/useTakeOverCommunityScheduledPostMutation';
import { SchedulePostPanel } from './SchedulePostPanel';

interface ScheduledQueuePanelProps {
  /** Unset while the queue loads. */
  posts?: CommunityScheduledPost[];
  nextUsualTime?: Date;
  currentMemberId?: string;
  isAdmin?: boolean;
  onBack: () => void;
  onSchedule: () => void;
  /** Opens the composer on the post, after the member agrees to discard a draft when there is one. */
  onEdit: (post: CommunityScheduledPost) => void;
  hasDraft: () => boolean;
  /** Whether the picker that reschedules a post focuses its field. */
  autoFocus: boolean;
}

// Takes the composer's place in its dialog, like the schedule picker. Rescheduling a post swaps the queue for the
// picker, which comes back to the queue.
export const ScheduledQueuePanel: FC<ScheduledQueuePanelProps> = ({
  posts,
  nextUsualTime,
  currentMemberId,
  isAdmin,
  onBack,
  onSchedule,
  onEdit,
  hasDraft,
  autoFocus,
}) => {
  const { t, i18n } = useTranslation();
  const deleteMutation = useDeleteCommunityScheduledPostMutation();
  const publishMutation = usePublishCommunityScheduledPostMutation();
  const rescheduleMutation = useRescheduleCommunityScheduledPostMutation();
  const takeOverMutation = useTakeOverCommunityScheduledPostMutation();
  const {
    queueActions,
    reschedulingPost,
    closeReschedule,
    deletingPost,
    closeDelete,
    pendingEditPost,
    confirmDiscard,
    closeDiscard,
    takeoverPost,
    confirmTakeover,
    closeTakeover,
  } = useScheduledQueueFlow({
    hasDraft,
    onEdit,
    takeOver: async (post) => (await takeOverMutation.mutateAsync(post.id)).scheduled_post,
  });

  if (reschedulingPost) {
    return (
      <SchedulePostPanel
        value={new Date(reschedulingPost.scheduled_at)}
        nextUsualTime={nextUsualTime}
        onConfirm={(scheduledAt) => {
          rescheduleMutation.mutate({ scheduledPostId: reschedulingPost.id, scheduledAt });
          closeReschedule();
        }}
        onCancel={closeReschedule}
        autoFocus={autoFocus}
      />
    );
  }

  return (
    <>
      <CommunityScheduledQueue
        posts={posts}
        currentMemberId={currentMemberId}
        isAdmin={isAdmin}
        onBack={onBack}
        onSchedule={onSchedule}
        {...queueActions}
        onPublishNow={(post) => publishMutation.mutate(post.id)}
        locale={i18n.language}
        translate={(key, values) => t(key, values)}
        // The dialog title names the composer's dialog after the queue's visible title.
        titleAs={HeadlessDialog.Title}
      />
      <ConfirmDialog
        isOpen={Boolean(deletingPost)}
        title={t('community.action_menu.delete_post_confirm_title')}
        description={t('community.action_menu.delete_post_confirm_description')}
        confirmLabel={t('community.action_menu.delete_post_confirm_button')}
        isDestructive
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (!deletingPost) return;
          deleteMutation.mutate(deletingPost.id, { onSettled: closeDelete });
        }}
        onCancel={closeDelete}
      />
      <ConfirmDialog
        isOpen={Boolean(pendingEditPost)}
        title={t('community.composer.schedule.queue.discard_title')}
        description={t('community.composer.schedule.queue.discard_description')}
        confirmLabel={t('community.composer.schedule.queue.discard_confirm')}
        isDestructive
        onConfirm={confirmDiscard}
        onCancel={closeDiscard}
      />
      <ConfirmDialog
        isOpen={Boolean(takeoverPost)}
        title={t('community.composer.schedule.queue.takeover_title')}
        description={t(
          takeoverPost?.takeover_mode === 'required'
            ? 'community.composer.schedule.queue.takeover_description'
            : 'community.composer.schedule.queue.takeover_off_team_description',
        )}
        confirmLabel={t('community.composer.schedule.queue.takeover_confirm')}
        isLoading={takeOverMutation.isPending}
        onConfirm={confirmTakeover}
        onCancel={closeTakeover}
      />
    </>
  );
};
